import React from 'react';
import { Link } from 'react-router-dom';
import {
  Plane,
  Boxes,
  Truck,
  Calculator,
  FileText,
  ShieldCheck,
  ClipboardCheck,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Phone,
  MessageSquare,
  HelpCircle,
  Building2,
  Cpu
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../components/ui/accordion';
import usePageSeo from '../hooks/usePageSeo';
import JsonLd from '../components/JsonLd';
import { breadcrumbSchema, serviceSchema, faqSchema } from '../utils/seoSchemas';
import RelatedServices from '../components/RelatedServices';

const SAIFZoneServices = () => {
  usePageSeo({
    title: 'SAIF Zone Audit & Accounting Services in Sharjah UAE',
    description:
      'Audit, accounting, VAT, Corporate Tax and AML compliance services for Sharjah Airport International Free Zone (SAIF Zone) companies in the UAE.',
    path: '/saif-zone-services'
  });

  const services = [
    {
      icon: ClipboardCheck,
      title: 'SAIF Zone Audit Services',
      description:
        'Annual audited financial statements prepared for SAIF Zone licence renewal and regulatory submissions, in line with IFRS and UAE requirements.',
    },
    {
      icon: Calculator,
      title: 'Accounting & Bookkeeping',
      description:
        'Cloud-based bookkeeping (Zoho / QuickBooks) for trading, e-commerce, logistics and aviation-related businesses based in SAIF Zone.',
    },
    {
      icon: FileText,
      title: 'Corporate Tax Services',
      description:
        'Corporate Tax registration, Qualifying Free Zone Person assessment, Small Business Relief evaluation and annual CT return filing.',
    },
    {
      icon: ShieldCheck,
      title: 'VAT Compliance',
      description:
        'VAT registration, quarterly filing, reconciliation and FTA correspondence — including correct treatment of cross-border and re-export supplies.',
    },
    {
      icon: AlertTriangle,
      title: 'AML / goAML Compliance',
      description:
        'AML frameworks, goAML registration and ongoing DNFBP obligations for regulated SAIF Zone activities.',
    },
    {
      icon: Building2,
      title: 'Renewal & Advisory Support',
      description:
        'Support during SAIF Zone licence renewal — audit coordination, financial statement formatting and authority follow-up.',
    }
  ];

  // SAIF-Zone-specific challenges (DIFFERENT from Hamriyah)
  const challenges = [
    {
      title: 'SAIF Renewal Audit Requirements',
      description: 'SAIF Zone routinely asks for audited financial statements at renewal. Late or non-compliant audit reports delay licence renewal and can trigger penalties.'
    },
    {
      title: 'VAT for Trading & Re-Export Entities',
      description: 'SAIF Zone is heavily populated by trading and re-export businesses. Correct VAT treatment (zero-rating, designated zone rules, export evidence) is a recurring FTA focus area.'
    },
    {
      title: 'ESR & Corporate Tax Considerations',
      description: 'Holding, IP, distribution and headquarters entities in SAIF Zone often trigger Economic Substance Regulations and need careful Corporate Tax substance analysis.'
    },
    {
      title: 'E-Commerce & Marketplace Bookkeeping',
      description: 'Online sellers operating from SAIF Zone deal with multi-channel revenue (Amazon, Noon, Shopify) — reconciling payouts, fees and refunds is rarely clean.'
    },
    {
      title: 'Aviation, Logistics & Multi-Currency Accounts',
      description: 'Proximity to Sharjah International Airport means many SAIF Zone businesses handle multi-currency transactions and cross-border services that need careful accounting.'
    }
  ];

  const sectors = [
    { name: 'Trading & Re-Export', icon: Boxes },
    { name: 'E-Commerce & Online Retail', icon: Cpu },
    { name: 'Aviation & Aerospace', icon: Plane },
    { name: 'Logistics & Freight Forwarding', icon: Truck },
    { name: 'IT & Technology', icon: Cpu },
    { name: 'Consulting & Services', icon: Building2 }
  ];

  const whyChoose = [
    'Audit reports prepared in the format SAIF Zone expects at renewal',
    'Strong VAT expertise for trading and re-export businesses',
    'Bookkeeping support for e-commerce and multi-channel sellers',
    'Joined-up Corporate Tax, ESR and AML compliance',
    'Direct, partner-level engagement',
    'Fixed-fee SME packages — no billing surprises'
  ];

  // SAIF-Zone-specific FAQs
  const faqs = [
    {
      q: 'Are audits mandatory for SAIF Zone companies at renewal?',
      a: 'SAIF Zone generally requires audited financial statements at the time of licence renewal for most licence categories. Missing or unsigned audit reports are the most common reason for renewal delays. WPAA prepares the audit in the format SAIF Zone expects.'
    },
    {
      q: 'How does VAT apply to SAIF Zone trading entities?',
      a: 'SAIF Zone is partially treated as a Designated Zone for VAT purposes on goods, while services follow mainland rules. Re-export supplies can be zero-rated when proper export evidence is retained. We review your contracts and flows to apply the right treatment and protect your input VAT.'
    },
    {
      q: 'Do SAIF Zone companies need to register for Corporate Tax?',
      a: 'Yes — registration with the FTA is mandatory for SAIF Zone entities. Whether you pay 0% as a Qualifying Free Zone Person or 9% on taxable income depends on substance, qualifying income and de minimis rules. WPAA assesses your eligibility and files your CT return.'
    },
    {
      q: 'Do I need to file ESR notifications from SAIF Zone?',
      a: 'If your SAIF Zone entity carries on a Relevant Activity (e.g., distribution, headquarters, holding, IP, service centre), it may have Economic Substance Regulations notification and reporting obligations. We assess scope and handle filings.'
    },
    {
      q: 'Can you help e-commerce businesses based in SAIF Zone?',
      a: 'Yes. E-commerce is one of our core specialisations. We reconcile marketplace payouts (Amazon, Noon, Shopify), structure books for VAT and Corporate Tax, and prepare audit-ready financials.'
    },
    {
      q: 'How fast can you complete a SAIF Zone audit?',
      a: 'For SMEs with reasonably maintained books, an audit can typically be completed within 2 to 4 weeks. Backlog clean-up engagements take longer — we always give a clear timeline up front.'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="saif-page">
      <JsonLd
        id="ld-saif-service"
        data={serviceSchema({
          name: 'SAIF Zone Audit, Accounting & Tax Services',
          description:
            'Audit, accounting, VAT, Corporate Tax and AML compliance services for Sharjah Airport International Free Zone (SAIF Zone) companies in the UAE.',
          path: '/saif-zone-services',
          serviceType: 'Free Zone Compliance Services - SAIF Zone'
        })}
      />
      <JsonLd
        id="ld-saif-breadcrumb"
        data={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'UAE Free Zone Services', path: '/free-zone-services-uae' },
          { name: 'SAIF Zone', path: '/saif-zone-services' }
        ])}
      />
      <JsonLd id="ld-saif-faq" data={faqSchema(faqs)} />

      {/* HERO */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-slate-900 via-indigo-900 to-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-indigo-400 rounded-full filter blur-3xl"></div>
        </div>
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-2 mb-6">
              <Plane className="text-blue-400" size={32} />
              <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">
                SAIF Zone — Sharjah
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              SAIF Zone Audit & Accounting Services in Sharjah UAE
            </h1>
            <p className="text-2xl text-blue-200 mb-6 font-semibold">
              Compliance Support for Sharjah Airport International Free Zone Companies
            </p>
            <p className="text-lg text-gray-300 mb-8 leading-relaxed max-w-3xl">
              From e-commerce and trading firms to aviation, logistics and IT companies, WPAA delivers practical
              audit, accounting, VAT and Corporate Tax support for businesses licensed by Sharjah Airport
              International Free Zone (SAIF Zone).
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact?service=saif-zone">
                <Button size="lg" className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6 text-lg" data-testid="saif-consultation-btn">
                  Book Free Consultation
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" data-testid="saif-whatsapp-btn">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  WhatsApp Us
                </Button>
              </a>
              <a href="tel:+971501219341" data-testid="saif-call-btn">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  Call Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* AUTHORITY CONTEXT */}
      <section className="py-16 px-4 bg-white border-b border-gray-100">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">About the Authority</span>
              <h2 className="text-3xl font-bold text-gray-900 mt-3 mb-4">Sharjah Airport International Free Zone</h2>
              <p className="text-gray-600 leading-relaxed mb-3">
                Sharjah Airport International Free Zone (SAIF Zone) sits adjacent to Sharjah International Airport
                and is one of the UAE&apos;s most active free zones for trading, re-export, logistics, aviation and
                e-commerce businesses. Its airport access and 24/7 customs make it a magnet for fast-moving goods
                and services.
              </p>
              <p className="text-gray-600 leading-relaxed">
                These business models bring specific compliance pressures: re-export VAT documentation, ESR
                analysis for distribution and headquarters entities, Corporate Tax substance, and a strict
                expectation of audited financials at renewal.
              </p>
            </div>
            <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100">
              <h3 className="font-bold text-gray-900 mb-3">Typical SAIF Zone Compliance Cycle</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex gap-2"><CheckCircle size={16} className="text-blue-600 mt-0.5 flex-shrink-0" /> Monthly bookkeeping &amp; reconciliations</li>
                <li className="flex gap-2"><CheckCircle size={16} className="text-blue-600 mt-0.5 flex-shrink-0" /> Quarterly VAT return filing</li>
                <li className="flex gap-2"><CheckCircle size={16} className="text-blue-600 mt-0.5 flex-shrink-0" /> Annual audited financial statements</li>
                <li className="flex gap-2"><CheckCircle size={16} className="text-blue-600 mt-0.5 flex-shrink-0" /> Annual Corporate Tax return</li>
                <li className="flex gap-2"><CheckCircle size={16} className="text-blue-600 mt-0.5 flex-shrink-0" /> ESR / AML notifications where applicable</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Services</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-4">What WPAA Delivers in SAIF Zone</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, idx) => {
              const Icon = svc.icon;
              return (
                <Card key={idx} className="border-2 hover:border-blue-500 transition-all hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className="bg-blue-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="text-blue-600" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{svc.title}</h3>
                    <p className="text-gray-600">{svc.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CHALLENGES */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Common Pain Points</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-4">
              Where SAIF Zone Companies Need Help
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Recurring compliance and reporting issues we see with SAIF Zone clients.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {challenges.map((c, idx) => (
              <Card key={idx} className="border border-gray-100 hover:shadow-md transition-all">
                <CardContent className="p-6 flex gap-4">
                  <AlertTriangle className="text-amber-500 flex-shrink-0 mt-1" size={22} />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">{c.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{c.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Sectors</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-4">Sectors We Serve in SAIF Zone</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {sectors.map((s, idx) => {
              const Icon = s.icon;
              return (
                <div key={idx} className="bg-white rounded-xl p-5 text-center border border-gray-100 hover:border-blue-500 transition-all">
                  <Icon className="text-blue-600 mx-auto mb-2" size={28} />
                  <p className="font-semibold text-gray-800 text-sm">{s.name}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY WPAA */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Why WPAA</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">
                Why SAIF Zone Companies Choose WPAA
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                A Sharjah-based audit and advisory firm that understands the SAIF Zone business mix and what its
                authority expects at renewal.
              </p>
            </div>
            <div className="space-y-3">
              {whyChoose.map((w, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">{w}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">FAQs</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">SAIF Zone — FAQs</h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white border border-gray-200 rounded-xl px-6 shadow-sm"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-blue-600 hover:no-underline">
                    <div className="flex items-start gap-3">
                      <HelpCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                      <span>{faq.q}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-base leading-relaxed pl-8">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <RelatedServices
        services={[
          { title: 'UAE Free Zone Services', description: 'Master page covering audit, tax and compliance across all UAE free zones.', path: '/free-zone-services-uae', accent: 'teal' },
          { title: 'Hamriyah Free Zone Services', description: 'Dedicated audit, VAT and CT support for Hamriyah Free Zone entities.', path: '/hamriyah-free-zone-services', accent: 'emerald' },
          { title: 'VAT Services in UAE', description: 'FTA-compliant VAT registration, filing and review.', path: '/vat-services-uae', accent: 'blue' }
        ]}
      />

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-700 to-indigo-700 text-white">
        <div className="container mx-auto text-center max-w-4xl">
          <h2 className="text-4xl font-bold mb-6">Talk to a SAIF Zone Specialist</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Book a free consultation and we&apos;ll review your SAIF Zone audit, VAT and Corporate Tax position.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
            <Link to="/contact?service=saif-zone">
              <Button size="lg" className="bg-white text-blue-700 hover:bg-gray-100 px-8 py-6 text-lg" data-testid="saif-cta-consultation-btn">
                Schedule Consultation
              </Button>
            </Link>
            <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" data-testid="saif-cta-whatsapp-btn">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                WhatsApp Us
              </Button>
            </a>
          </div>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 text-blue-100">
            <a href="tel:+971509987431" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="saif-cta-phone-1">
              <Phone size={18} />
              <span>+971 50 998 7431</span>
            </a>
            <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="saif-cta-phone-2">
              <MessageSquare size={18} />
              <span>WhatsApp +971 50 121 9341</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SAIFZoneServices;
